import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Sunset } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { searchGallery, fetchGalleryImageBlob } from '../../lib/api';

/**
 * GalleryPickerModal — Cloudinary/curated gallery browser used from
 * AddPrayer's Cover Image section.
 *
 * The parent owns the imageFile/imagePreview state — this modal is
 * completely self-contained around the gallery browse experience.
 * When the user picks an image, we fetch the blob, build a File +
 * object URL, and hand both to the parent via `onPick(file, previewUrl)`
 * so the parent can splice into its own image state (with correct
 * URL.revokeObjectURL cleanup on the previous preview).
 *
 * Extracted from AddPrayer.jsx (Phase A1 of the P2 refactor) — no
 * behavior changes. All `data-testid` attributes preserved so existing
 * tests keep passing verbatim.
 */
export const GalleryPickerModal = ({ open, onOpenChange, onPick }) => {
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pickingId, setPickingId] = useState(null);
  const sentinelRef = useRef(null);

  const loadPage = useCallback(async (nextPage) => {
    setLoading(true);
    setError(null);
    try {
      const data = await searchGallery({ page: nextPage, page_size: 24 });
      const results = Array.isArray(data.results) ? data.results : [];
      setImages((prev) => (nextPage === 1 ? results : [...prev, ...results]));
      const hasMoreServer = typeof data.has_more === 'boolean'
        ? data.has_more
        : ((data.page_count || 0) === 0 || nextPage < (data.page_count || 0));
      setHasMore(results.length > 0 && hasMoreServer);
      setPage(nextPage);
    } catch (e) {
      console.error('gallery load failed', e);
      setError(e.response?.data?.detail || 'Could not load the gallery. Please try again.');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Kick off the first page when the dialog opens for the first time.
  useEffect(() => {
    if (open && images.length === 0) loadPage(1);
  }, [open, loadPage, images.length]);

  // Infinite scroll — observe the sentinel that lives just below the
  // grid. When it enters the viewport and we're not already loading /
  // out of pages, fetch the next page.
  useEffect(() => {
    if (!open) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && !loading && hasMore) {
        loadPage(page + 1);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, loading, hasMore, page, loadPage]);

  const pickImage = async (item) => {
    if (pickingId) return;
    setPickingId(item.id);
    try {
      const blob = await fetchGalleryImageBlob(item.url);
      const ext = (blob.type && blob.type.split('/')[1]) || 'jpg';
      const file = new File([blob], `gallery-${item.id || Date.now()}.${ext}`, { type: blob.type || 'image/jpeg' });
      const previewUrl = URL.createObjectURL(file);
      onPick(file, previewUrl);
      onOpenChange(false);
    } catch (e) {
      console.error('gallery pick failed', e);
      setError(e.response?.data?.detail || 'Could not use that image. Please try another.');
    } finally {
      setPickingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="sunrise-gallery-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sunset size={18} className="text-amber-500" /> Gallery
          </DialogTitle>
          <DialogDescription>
            Curated images from the Pray Now Together gallery. Tap one to use it as your cover.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 py-2" data-testid="sunrise-gallery-scroll">
          {error && images.length === 0 ? (
            <div className="py-10 text-center text-sm text-red-600 dark:text-red-400" data-testid="gallery-error">
              {error}
              <div className="mt-2">
                <button onClick={() => loadPage(1)} className="text-sm underline text-zinc-700 dark:text-zinc-200">Try again</button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {images.map((item) => {
                  const picking = pickingId === item.id;
                  return (
                    <button
                      key={item.id || item.url}
                      type="button"
                      onClick={() => pickImage(item)}
                      disabled={Boolean(pickingId)}
                      data-testid={`gallery-item-${item.id || 'x'}`}
                      title={item.title || 'Untitled'}
                      className="relative group aspect-square rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 hover:ring-2 hover:ring-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400 transition disabled:opacity-60"
                    >
                      <img
                        src={item.thumbnail}
                        alt={item.title || 'Sunrise or sunset'}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.currentTarget.style.opacity = '0.35'; }}
                      />
                      {picking && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Loader2 className="animate-spin text-white" size={24} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {error && images.length > 0 && (
                <div className="mt-3 p-2 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs text-center" data-testid="gallery-error-inline">
                  {error}
                </div>
              )}

              <div ref={sentinelRef} className="py-6 flex items-center justify-center text-sm text-zinc-500" data-testid="gallery-sentinel">
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading more…</span>
                ) : hasMore ? (
                  <span>Scroll for more</span>
                ) : images.length > 0 ? (
                  <span>You&apos;ve reached the end.</span>
                ) : (
                  <span>&nbsp;</span>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-zinc-200 dark:border-zinc-800">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="close-sunrise-gallery-btn"
            className="h-9 px-4 rounded-md text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GalleryPickerModal;
